import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  addEdge,
  useNodesState,
  useEdgesState,
  useNodesInitialized,
  useReactFlow,
  BackgroundVariant,
  MarkerType,
} from '@xyflow/react'
import { GRAPH_MULTISELECT_PROPS } from '../lib/graphInteractionProps'
import './nodes/nodeLook.css'
import { RightDragPan } from './RightDragPan'
import '@xyflow/react/dist/style.css'
import { useCanvasFocus, selectOnlyChanges }  from '../lib/useGraphFocus'
import { useRememberedViewport } from '../lib/useGraphViewport'
import { StateNode }       from './nodes/StateNode'
import { EntryNode }       from './nodes/EntryNode'
import { TransitionEdge }  from './nodes/TransitionEdge'
import { PlayClipNode }    from './nodes/PlayClipNode'
import { BlendNode }       from './nodes/BlendNode'
import { RandomNode }      from './nodes/RandomNode'
import { SelectNode }      from './nodes/SelectNode'
import { ProbabilitySelectNode } from './nodes/ProbabilitySelectNode'
import { defaultOptions as defaultProbabilityOptions } from '../lib/probabilitySelect'
import { StateOutputNode } from './nodes/StateOutputNode'
import { GetVariableNode } from './nodes/GetVariableNode'
import { LayeredBoneBlendNode } from './nodes/LayeredBoneBlendNode'
import { EventActionsPanel } from './EventActionsPanel'
import { GraphHeaderBar, CanvasHint, headerBtn, headerLabel } from './GraphHeaderBar'
import { customEventNames } from '../lib/customEvents'
import { eventGraphState } from '../lib/eventGraphState'
import { initSM, getCurrentState, smRuntime } from '../lib/smRuntime'
import { debugState } from '../lib/debugState'
import {
  getSmSplineStyle, setSmSplineStyle, SPLINE_STYLES,
  getSmSplineCurvature, setSmSplineCurvature, SPLINE_CURVATURE_STEP,
  curvatureToNormalized, normalizedToCurvature,
} from '../lib/smSplineStyle'
import { nodeRect, pickPins } from '../lib/smPinRouting'
import { createSmNodeCache } from '../lib/smNodeCache'
import { SM_THEME as T } from './nodes/stateMachineTheme'
import AnchoredOverlay from './AnchoredOverlay'

const SM_NODE_TYPES = {
  state: StateNode,
  entry: EntryNode,
}

const SM_EDGE_TYPES = {
  transition: TransitionEdge,
}

const STATE_INNER_NODE_TYPES = {
  playClip:    PlayClipNode,
  blend:       BlendNode,
  random:      RandomNode,
  select:      SelectNode,
  probabilitySelect: ProbabilitySelectNode,
  getVariable: GetVariableNode,
  layeredBoneBlend: LayeredBoneBlendNode,
  stateOutput: StateOutputNode,
}

const DEFAULT_STATE_INNER_NODES = () => [{
  id: 'output',
  type: 'stateOutput',
  position: { x: 350, y: 120 },
  data: {},
  deletable: false,
}]

let _stateId = 1
const nextStateId = () => `state_${_stateId++}`

// ENG-1072: handle ids are no longer normalized here. A transition's pins are
// chosen per render from the live geometry of the two nodes (see pickPins), so
// whatever a saved edge carries — the legacy single 'out'/'in' pair, or the '-'
// older writes used — is simply overridden. Nothing needs migrating.
function normalizeSMEdges(edges) {
  return (edges ?? []).map(e => ({
    ...e,
    type: e.type ?? 'transition',
    markerEnd: e.markerEnd ?? { type: MarkerType.ArrowClosed, color: T.marker },
    data: e.data ?? {},
  }))
}

// ── Helper: find the first PlayClip's clipName inside a state inner graph ──
function getStateClipName(stateNode) {
  const sEdges = stateNode?.data?.innerEdges ?? []
  const sNodes = stateNode?.data?.innerNodes ?? []
  const outEdge = sEdges.find(e => e.target === 'output')
  if (!outEdge) return null
  const src = sNodes.find(n => n.id === outEdge.source)
  if (src?.type === 'playClip') return src.data?.clipName ?? null
  return null
}

// ─── State inner canvas ───────────────────────────────────────────────────────
function StateInnerCanvas({ nodes, edges, onNodesChange, onEdgesChange, onConnect, onAdd }) {
  const { screenToFlowPosition } = useReactFlow()
  const [contextMenu, setContextMenu] = useState(null)

  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault()
    const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY })
    setContextMenu({ screenX: e.clientX, screenY: e.clientY, flowPos })
  }, [screenToFlowPosition])

  const closeMenu = useCallback(() => setContextMenu(null), [])

  useEffect(() => {
    if (!contextMenu) return
    const h = () => closeMenu()
    window.addEventListener('mousedown', h)
    return () => window.removeEventListener('mousedown', h)
  }, [contextMenu, closeMenu])

  const menuItem = (label, type) => (
    <div
      key={type}
      onClick={() => { onAdd(type, contextMenu.flowPos); closeMenu() }}
      style={{
        padding: '7px 12px',
        cursor: 'pointer',
        color: T.btnText,
        fontSize: 11,
        borderLeft: `3px solid ${T.btnBorder}`,
        margin: '3px 6px',
        borderRadius: '0 4px 4px 0',
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#1f1f1f'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {label}
    </div>
  )

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onPaneContextMenu={onPaneContextMenu}
        onPaneClick={closeMenu}
        nodeTypes={STATE_INNER_NODE_TYPES}
        // A state's inner graph is an animation graph: same node look as the
        // body / face graphs (nodeLook.css).
        className="aos-graph-anim"
        fitView
        fitViewOptions={{ padding: 0.3 }}
        deleteKeyCode="Delete"
        style={{ background: T.canvasBg }}
        {...GRAPH_MULTISELECT_PROPS}
      >
        <Background variant={BackgroundVariant.Dots} color={T.dots} gap={20} size={1} />
        <Controls style={{ background: T.btnBg, border: `1px solid ${T.btnBorder}`, color: T.btnText }} />
        <RightDragPan />
      </ReactFlow>

      {contextMenu && (
        <AnchoredOverlay
          x={contextMenu.screenX} y={contextMenu.screenY}
          style={{
            background: '#141414',
            border: '1px solid #303030',
            borderRadius: 6,
            boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
            zIndex: 1000,
            overflow: 'hidden',
            fontFamily: 'var(--m-font)',
            minWidth: 140,
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div style={{ padding: '4px 0' }}>
            {menuItem('+ Play Clip', 'playClip')}
            {menuItem('+ Blend',     'blend')}
            {menuItem('+ Layered Bone Blend', 'layeredBoneBlend')}
            {menuItem('+ Random',    'random')}
            {menuItem('+ Select',    'select')}
            {menuItem('+ Probability Select', 'probabilitySelect')}
            {menuItem('+ Get Variable', 'getVariable')}
          </div>
        </AnchoredOverlay>
      )}
    </>
  )
}

// ─── StateInnerEditor ─────────────────────────────────────────────────────────
function StateInnerEditor({ stateId, stateName, smName, initialNodes, initialEdges, graphType, onBack, onGraphChange, onEnter = [], onExit = [], onStateDataChange }) {
  const initNodes = (initialNodes?.length > 0 ? initialNodes : DEFAULT_STATE_INNER_NODES())
  const [nodes, setNodes, onNodesChange] = useNodesState(initNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges ?? [])
  // Read once per open, like the transition editor does: the event graph is a
  // different editor, so its custom events cannot change while this one is up.
  const availableCustomEvents = useMemo(() => customEventNames(eventGraphState.nodes), [])

  useEffect(() => {
    onGraphChange?.(nodes, edges)
  }, [nodes, edges]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateNode = useCallback((nodeId, key, value) => {
    setNodes(ns => ns.map(n => n.id === nodeId ? { ...n, data: { ...n.data, [key]: value } } : n))
  }, [setNodes])

  const nodesWithHandlers = nodes.map(n => ({
    ...n,
    data: {
      ...n.data,
      _graphType: graphType,
      connected: n.id === 'output' ? edges.some(e => e.target === 'output') : n.data.connected,
      onChange: n.type !== 'stateOutput' ? (key, val) => updateNode(n.id, key, val) : undefined,
    },
  }))

  const onConnect = useCallback((params) => {
    setEdges(es => {
      const filtered = es.filter(e => !(e.target === params.target && e.targetHandle === params.targetHandle))
      return addEdge({ ...params }, filtered)
    })
  }, [setEdges])

  const addNode = useCallback((type, position) => {
    const id = `sn_${Date.now()}`
    const pos = position ?? { x: 150, y: 100 }
    if (type === 'playClip') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { clipName: '', playSpeed: 1.0 } }])
    } else if (type === 'blend') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { alpha: 0.5 } }])
    } else if (type === 'random') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { randomType: 'int', min: 0, max: 1 } }])
    } else if (type === 'select') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { selectType: 'animation', optionCount: 2, index: 0 } }])
    } else if (type === 'probabilitySelect') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { options: defaultProbabilityOptions(2) } }])
    } else if (type === 'getVariable') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { variableName: '' } }])
    } else if (type === 'layeredBoneBlend') {
      setNodes(ns => [...ns, { id, type, position: pos, data: { blendBones: ['clavicle_l'], blendWeight: 1.0, blendMode: 'additive', additiveRef: 'firstFrame' } }])
    }
  }, [setNodes])

  return (
    <div style={{ width: '100%', height: '100%', background: T.canvasBg, display: 'flex', flexDirection: 'column' }}>
      <GraphHeaderBar onBack={onBack} crumbs={[smName, stateName]} />
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
      <CanvasHint>Right-click to add nodes</CanvasHint>

      {/* On Enter / On Exit (smStateEvents.js): custom events fired once when
          this state is entered and once when it is left — on a transition at
          the moment it is taken, and on a silent drop too. */}
      <div style={{
        position: 'absolute', top: 10, right: 10, zIndex: 10,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        <EventActionsPanel
          title="On Enter"
          hint="Custom events fired once when this state is entered."
          actions={onEnter}
          available={availableCustomEvents}
          onChange={list => onStateDataChange?.('onEnter', list)}
          testId="state-on-enter"
        />
        <EventActionsPanel
          title="On Exit"
          hint="Custom events fired once when this state is left, including when it is dropped (e.g. a blueprint switch)."
          actions={onExit}
          available={availableCustomEvents}
          onChange={list => onStateDataChange?.('onExit', list)}
          testId="state-on-exit"
        />
      </div>

      <ReactFlowProvider>
        <StateInnerCanvas
          nodes={nodesWithHandlers}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onAdd={addNode}
        />
      </ReactFlowProvider>
      </div>
    </div>
  )
}

// ─── SM flow canvas ───────────────────────────────────────────────────────────
function SMFlowCanvas({ nodes, edges, onNodesChange, onEdgesChange, onConnect, onAddState, onEdgeMouseEnter, onEdgeMouseLeave, graphType = 'body', smNodeId = null }) {
  const { screenToFlowPosition } = useReactFlow()
  const [contextMenu, setContextMenu] = useState(null)
  // Serves a Ctrl+F / agent focus request for a state INSIDE this machine —
  // the parent editor drills in, this centres on it.
  useCanvasFocus({
    graph: graphType,
    containerId: smNodeId,
    onSelectNode: useCallback(
      (id) => onNodesChange(selectOnlyChanges(nodes, id)),
      [onNodesChange, nodes],
    ),
  })
  // A state machine is a canvas you close and reopen like any other.
  const viewportProps = useRememberedViewport({ surface: graphType, containerId: smNodeId })

  // ENG-1072: assign each transition its pins from the live geometry of the two
  // nodes it joins, which is why a wire jumps to the other side of a node
  // mid-drag. This has to happen INSIDE the provider: only ReactFlow's internal
  // nodes carry `measured`, and reading sizes off the `nodes` array we pass in
  // gets zeros. `nodesInitialized` is in the deps so the routing recomputes the
  // moment sizes land — before that the comparison falls back to top-left
  // corners, which is deterministic and self-correcting.
  const nodesInitialized = useNodesInitialized()
  const { getInternalNode } = useReactFlow()
  const routedEdges = useMemo(
    () => edges.map(e => {
      const source = nodeRect(getInternalNode(e.source))
      const target = nodeRect(getInternalNode(e.target))
      // A dangling edge (node deleted mid-save) keeps whatever pins it had
      // rather than crashing the canvas.
      return source && target ? { ...e, ...pickPins(source, target) } : e
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `nodes` and
    // `nodesInitialized` are the signals that geometry moved; getInternalNode
    // reads it imperatively and is stable.
    [edges, nodes, nodesInitialized, getInternalNode],
  )

  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault()
    const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY })
    setContextMenu({ screenX: e.clientX, screenY: e.clientY, flowPos })
  }, [screenToFlowPosition])

  const closeMenu = useCallback(() => setContextMenu(null), [])

  useEffect(() => {
    if (!contextMenu) return
    const h = () => closeMenu()
    window.addEventListener('mousedown', h)
    return () => window.removeEventListener('mousedown', h)
  }, [contextMenu, closeMenu])

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={routedEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgeMouseEnter={onEdgeMouseEnter}
        onEdgeMouseLeave={onEdgeMouseLeave}
        onPaneContextMenu={onPaneContextMenu}
        onPaneClick={closeMenu}
        nodeTypes={SM_NODE_TYPES}
        edgeTypes={SM_EDGE_TYPES}
        {...viewportProps}
        fitViewOptions={{ padding: 0.3 }}
        deleteKeyCode="Delete"
        style={{ background: T.canvasBg }}
        {...GRAPH_MULTISELECT_PROPS}
      >
        <Background variant={BackgroundVariant.Dots} color={T.dots} gap={20} size={1} />
        <Controls style={{ background: T.btnBg, border: `1px solid ${T.btnBorder}`, color: T.btnText }} />
        <RightDragPan />
      </ReactFlow>

      {contextMenu && (
        <AnchoredOverlay
          x={contextMenu.screenX} y={contextMenu.screenY}
          style={{
            background: '#141414',
            border: '1px solid #303030',
            borderRadius: 6,
            boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
            zIndex: 1000,
            overflow: 'hidden',
            fontFamily: 'var(--m-font)',
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div
            onClick={() => { onAddState(contextMenu.flowPos); closeMenu() }}
            style={{
              padding: '8px 14px', cursor: 'pointer', color: T.btnText, fontSize: 12,
              borderLeft: `3px solid ${T.btnBorder}`, margin: '4px 6px', borderRadius: '0 4px 4px 0',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#1f1f1f'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            + State
          </div>
        </AnchoredOverlay>
      )}
    </>
  )
}

// ─── StateMachineEditor ───────────────────────────────────────────────────────
export function StateMachineEditor({
  smNodeId,
  smName,
  initialNodes = [],
  initialEdges = [],
  graphType = 'body',
  onBack,
  onGraphChange,
  onOpenRule,
  ruleResults = {},
}) {
  const [innerNodes, setInnerNodes, onInnerNodesChange] = useNodesState(initialNodes)
  const [innerEdges, setInnerEdges, onInnerEdgesChange] = useEdgesState(normalizeSMEdges(initialEdges))

  // SM-level view stack: sm | state
  const [viewStack, setViewStack] = useState([{ type: 'sm' }])
  const currentView = viewStack[viewStack.length - 1]
  const pushView = (v) => setViewStack(s => [...s, v])
  const popView  = ()  => setViewStack(s => s.slice(0, -1))

  const [activeStateId, setActiveStateId] = useState(() => getCurrentState(smNodeId))
  const activeStateRef = useRef(activeStateId)
  const [clipTimings, setClipTimings] = useState({})

  // ENG-1061: transition spline shape (view preference, persisted per-user).
  const [splineStyle, setSplineStyle] = useState(getSmSplineStyle)
  const changeSplineStyle = useCallback((v) => {
    setSplineStyle(setSmSplineStyle(v))
  }, [])
  // Arched-spline bend tightness (ENG-1061 follow-up) — same persistence model.
  const [splineCurvature, setSplineCurvature] = useState(getSmSplineCurvature)
  const changeSplineCurvature = useCallback((v) => {
    setSplineCurvature(setSmSplineCurvature(v))
  }, [])

  // ENG-1061: hover-to-trace — hovering one transition (its line or its rule
  // badge) spotlights it and fades the rest, so a single connection is legible
  // even where many overlap. Plain UI state (not per-frame), fires on enter/leave.
  const [hoveredEdgeId, setHoveredEdgeId] = useState(null)
  const onEdgeEnter = useCallback((_e, edge) => setHoveredEdgeId(edge.id), [])
  const onEdgeLeave = useCallback(() => setHoveredEdgeId(null), [])

  // ENG-1061: persist a transition's reroute waypoints (Unreal-style). Writes
  // edge.data.waypoints; the existing onGraphChange effect auto-saves it.
  const setEdgeWaypoints = useCallback((edgeId, waypoints) => {
    setInnerEdges(es => es.map(e =>
      e.id === edgeId ? { ...e, data: { ...e.data, waypoints } } : e
    ))
  }, [setInnerEdges])

  // The blend chip's write, the same shape as waypoints above and for the same
  // reason: this editor owns `innerEdges`, and the onGraphChange effect below
  // hands the WHOLE list up to updateSMInnerGraph, which replaces it. A write
  // that went straight out to the parent instead would be read back from this
  // stale local copy — the chip would keep showing the old value — and the next
  // local edit (drag a state, drop a waypoint) would push that stale list up and
  // overwrite it. Write here; the effect persists it.
  const setEdgeDuration = useCallback((edgeId, seconds) => {
    setInnerEdges(es => es.map(e =>
      e.id === edgeId ? { ...e, data: { ...e.data, transitionDuration: seconds } } : e
    ))
  }, [setInnerEdges])

  useEffect(() => {
    onGraphChange?.(innerNodes, innerEdges)
  }, [innerNodes, innerEdges]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    initSM(smNodeId)
  }, [smNodeId])

  useEffect(() => {
    const id = setInterval(() => {
      const current = smRuntime[smNodeId]?.currentState ?? null
      if (current !== activeStateRef.current) {
        activeStateRef.current = current
        setActiveStateId(current)
      }
      setClipTimings({ ...debugState.clipTimings })
    }, 100)
    return () => clearInterval(id)
  }, [smNodeId])

  const updateInnerNode = useCallback((nodeId, key, value) => {
    setInnerNodes(ns => ns.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, [key]: value } } : n
    ))
  }, [setInnerNodes])

  const updateStateInnerGraph = useCallback((stateId, sNodes, sEdges) => {
    setInnerNodes(ns => ns.map(n =>
      n.id === stateId ? { ...n, data: { ...n.data, innerNodes: sNodes, innerEdges: sEdges } } : n
    ))
  }, [setInnerNodes])

  // ENG-1072: the nodes handed to ReactFlow must keep a stable identity between
  // renders or ReactFlow discards their measured geometry and silently drops
  // every edge. See lib/smNodeCache for the full story; the poll above makes
  // this component re-render 10× a second, which is what made it bite.
  const cacheRef = useRef(null)
  cacheRef.current ??= createSmNodeCache()
  const decorateNodes = cacheRef.current
  const nodesWithHandlers = decorateNodes(innerNodes, {
    activeStateId,
    clipTimings,
    getClipName:   (n) => (n.type === 'state' ? getStateClipName(n) : null),
    makeOnChange:  (n) => (key, value) => updateInnerNode(n.id, key, value),
    makeOnDrillIn: (n) => (n.type === 'state'
      ? () => pushView({ type: 'state', stateId: n.id, stateName: n.data?.name ?? 'State' })
      : undefined),
  })

  const edgesWithHandlers = innerEdges.map(e => {
    const hasBidirectional = e.source !== 'entry' && innerEdges.some(
      other => other.source === e.target && other.target === e.source
    )
    const labelOffset = hasBidirectional ? (e.source < e.target ? -22 : 22) : 0
    const hovered = e.id === hoveredEdgeId
    return {
      ...e,
      // ENG-1072: sourceHandle/targetHandle are assigned inside SMFlowCanvas,
      // where ReactFlow's measured geometry is reachable.
      // NOTE: deliberately NO per-edge zIndex lift on hover. Raising it makes
      // ReactFlow render the hovered edge's SVG (including the transparent fat
      // "double-click to add a waypoint" hit-path) in a layer ABOVE the HTML
      // waypoint handles, so you couldn't grab a dot while hovering its edge
      // (ENG-1061 review). Hover-to-trace still reads via brightness/opacity.
      data: {
        ...e.data,
        onOpenRule:  (edgeId) => onOpenRule?.(edgeId),
        // Blend chip: edits edge.data.transitionDuration in place, so a
        // machine's timing is readable and settable without drilling into
        // each transition's rule editor.
        onDurationChange: setEdgeDuration,
        isActive:    e.target === activeStateId,
        hasRule:     !!(e.data?.ruleGraph?.nodes?.length > 0),
        actionCount: e.data?.actions?.length ?? 0,   // ENG-1062
        ruleResult:  ruleResults[`${smNodeId}::${e.id}`] ?? null,
        isEntryEdge: e.source === 'entry',
        splineStyle,                                  // ENG-1061
        curvature: splineCurvature,                   // ENG-1061 arched bend tightness
        hovered,                                      // ENG-1061 hover-to-trace
        anyHovered:  hoveredEdgeId !== null,
        onHover:     setHoveredEdgeId,
        onHoverEnd:  onEdgeLeave,
        onWaypointsChange: setEdgeWaypoints,          // ENG-1061 reroute waypoints
        labelOffset,
      },
    }
  })

  const onConnect = useCallback((params) => {
    setInnerEdges(es => {
      let filtered = es
      if (params.source === 'entry') {
        filtered = es.filter(e => e.source !== 'entry')
      }
      return addEdge({
        ...params,
        type: 'transition',
        markerEnd: { type: MarkerType.ArrowClosed, color: T.marker },
        data: {},
      }, filtered)
    })
  }, [setInnerEdges])

  const addState = useCallback((position) => {
    const id = nextStateId()
    setInnerNodes(ns => [...ns, {
      id,
      type: 'state',
      position: position ?? { x: 200 + Math.random() * 200, y: 80 + Math.random() * 200 },
      data: {
        name: 'New State',
        innerNodes: DEFAULT_STATE_INNER_NODES(),
        innerEdges: [],
      },
    }])
  }, [setInnerNodes])

  // Current state node for the state-inner view
  const currentStateNode = currentView.type === 'state'
    ? innerNodes.find(n => n.id === currentView.stateId)
    : null

  return (
    <div style={{ width: '100%', height: '100%', background: T.canvasBg, display: 'flex', flexDirection: 'column' }}>

      {/* ── SM view ── */}
      {currentView.type === 'sm' && (
        <>
          <GraphHeaderBar onBack={onBack} crumbs={['Blueprint', smName ?? 'State Machine']}>
            <button onClick={() => addState()} style={headerBtn}>+ State</button>

            {/* ENG-1061: transition spline shape — themed (tokens only) */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontFamily: 'var(--m-font)',
            }}>
              <span style={headerLabel}>Splines</span>
              <div style={{
                display: 'flex',
                border: '1px solid var(--m-line)',
                borderRadius: 'var(--m-radius-sm)',
                overflow: 'hidden',
              }}>
                {SPLINE_STYLES.map(s => {
                  const on = splineStyle === s.value
                  return (
                    <button
                      key={s.value}
                      onClick={() => changeSplineStyle(s.value)}
                      title={s.title}
                      style={{
                        background: on ? 'var(--m-accent)' : 'var(--m-panel)',
                        color: on ? 'var(--m-accent-ink)' : 'var(--m-fg-3)',
                        border: 'none',
                        padding: '5px 10px',
                        fontSize: 10,
                        fontFamily: 'var(--m-font)',
                        cursor: 'pointer',
                      }}
                    >
                      {s.label}
                    </button>
                  )
                })}
              </div>

              {/* Arched bend tightness — only meaningful for the arched style,
                  so it's disabled (and dimmed) under step/straight. */}
              {(() => {
                const archedOn = splineStyle === 'arched'
                return (
                  <div
                    title="How pronounced the bend is on arched splines"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, marginLeft: 4,
                      opacity: archedOn ? 1 : 0.4,
                    }}
                  >
                    <span style={headerLabel}>Bend</span>
                    {/* Normalized 0..1 scale; the stored value stays in real
                        curvature units (normalizedToCurvature maps between). */}
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={SPLINE_CURVATURE_STEP}
                      value={curvatureToNormalized(splineCurvature)}
                      disabled={!archedOn}
                      onChange={(e) => changeSplineCurvature(normalizedToCurvature(parseFloat(e.target.value)))}
                      style={{ width: 88, cursor: archedOn ? 'pointer' : 'default', accentColor: 'var(--m-accent)' }}
                    />
                    <span style={{
                      color: 'var(--m-fg-3)', fontSize: 10, width: 26, textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {curvatureToNormalized(splineCurvature).toFixed(2)}
                    </span>
                  </div>
                )
              })()}
            </div>
          </GraphHeaderBar>

          <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          <CanvasHint>Double-click a state to edit it · right-click to add · double-click a line to add a reroute point</CanvasHint>
          <ReactFlowProvider>
            <SMFlowCanvas
              nodes={nodesWithHandlers}
              edges={edgesWithHandlers}
              onNodesChange={onInnerNodesChange}
              onEdgesChange={onInnerEdgesChange}
              onConnect={onConnect}
              onAddState={addState}
              onEdgeMouseEnter={onEdgeEnter}
              onEdgeMouseLeave={onEdgeLeave}
              graphType={graphType}
              smNodeId={smNodeId}
            />
          </ReactFlowProvider>
          </div>
        </>
      )}

      {/* ── State inner graph view ── */}
      {currentView.type === 'state' && currentStateNode && (
        <StateInnerEditor
          key={currentView.stateId}
          stateId={currentView.stateId}
          stateName={currentView.stateName}
          smName={smName ?? 'State Machine'}
          initialNodes={currentStateNode.data?.innerNodes}
          initialEdges={currentStateNode.data?.innerEdges}
          graphType={graphType}
          onBack={popView}
          onGraphChange={(sNodes, sEdges) => updateStateInnerGraph(currentView.stateId, sNodes, sEdges)}
          onEnter={currentStateNode.data?.onEnter ?? []}
          onExit={currentStateNode.data?.onExit ?? []}
          onStateDataChange={(key, value) => updateInnerNode(currentView.stateId, key, value)}
        />
      )}
    </div>
  )
}
